import {
  matchIsValidTel,
  MuiTelInput,
  MuiTelInputCountry,
} from 'mui-tel-input';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { removeWhiteSpaces } from '../../../helpers/agentHelper';
import { useTranslation } from '../../../hooks/useTranslation';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import {
  getEnabledCountries,
  isValidPhoneNumberPrefix,
  getUnsupportedPhoneNumberTranslationKey,
} from '../../../interfaces/Country';
import StyledFormItem from '../../../styles/StyledFormItem';
import Text from '../../shared/Text/Text';

interface TelephoneInputProps {
  disabled?: boolean;
  hidden?: boolean;
  initialValue?: string;
  labelText?: string;
  name: string;
  required?: boolean;
  usedPhoneNumbers?: (string | undefined)[];
  usedPhoneNumbersErrorText?: string;
  marginBottom?: string;
}

const TelephoneInput: React.FC<TelephoneInputProps> = ({
  disabled = false,
  hidden = false,
  initialValue = '',
  labelText,
  name,
  required = false,
  usedPhoneNumbers = [],
  usedPhoneNumbersErrorText,
  marginBottom,
}) => {
  const { t } = useTranslation();
  const translatedLabelText = labelText || t('Phone Number');
  const translatedUsedPhoneNumbersErrorText =
    usedPhoneNumbersErrorText || t('This phone number is already in use');
  const { control } = useFormContext();
  const hasUSFISupport = useIsFeatureEnabled(
    FeatureGate.US_FI_PHONE_NUMBERS_SUPPORT
  );

  const enabledCountries = getEnabledCountries(hasUSFISupport);
  const listOfSupportedCountries: MuiTelInputCountry[] = Object.keys(
    enabledCountries
  ).map(country => enabledCountries[country].code);

  const validatePhoneNumber = (value: string) => {
    if (!value && !required) return true;
    if (value === '+' || value === '') return true;

    // Use the dynamic validation function
    if (!isValidPhoneNumberPrefix(value, hasUSFISupport)) {
      const translationKey =
        getUnsupportedPhoneNumberTranslationKey(hasUSFISupport);
      return t(translationKey as any);
    }

    if (usedPhoneNumbers.includes(removeWhiteSpaces(value))) {
      return translatedUsedPhoneNumbersErrorText;
    }

    return matchIsValidTel(value) || t('Invalid phone number');
  };

  return (
    <StyledFormItem hidden={hidden} flexGrow={1} marginBottom={marginBottom}>
      <Text type="label">{translatedLabelText}</Text>
      <Controller
        name={name}
        control={control}
        defaultValue={initialValue}
        rules={{
          required: required ? t('Phone number is required') : false,
          validate: validatePhoneNumber,
        }}
        render={({ field, fieldState: { error } }) => (
          <MuiTelInput
            {...field}
            value={field.value} // RHF manages the value
            onChange={field.onChange} // RHF handles onChange
            error={!!error}
            helperText={error?.message || ''}
            onlyCountries={listOfSupportedCountries}
            hidden={hidden}
            disabled={disabled}
          />
        )}
      />
    </StyledFormItem>
  );
};

export default TelephoneInput;
